import { Button, Text, Flex, Box } from '@chakra-ui/react';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import React, { ReactElement, ReactNode, useState } from 'react';
import { Breakpoints } from 'types/breakpoints';
import { ModalWorksheetDistributionSaveVariantProps, SharedModalProps } from 'types/Modal';
import { NovaIcon } from 'components/ui/novaIcon/NovaIcon';
import { ContentCopyIcon, DeleteIcon, FilesIcon } from 'assets/icons/v2';
import { CustomModal } from '../../CustomModal';
import { CustomModalContent } from '../../CustomModalContent';

type Props = SharedModalProps & ModalWorksheetDistributionSaveVariantProps;
type ModalMode = NonNullable<ModalWorksheetDistributionSaveVariantProps['mode']>;

export const WorksheetDistributionSaveModal = ({
  handleClose,
  isShown,
  onCopyAndSave,
  onPrimarySave,
  onCancel,
  mode = 'DISTRIBUTED',
}: Props): ReactElement => {
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleCopyAndSave = async (): Promise<void> => {
    if (!onCopyAndSave) return;
    setIsSubmitting(true);
    try {
      await onCopyAndSave();
      handleClose();
    } catch {
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrimarySave = async (): Promise<void> => {
    if (!onPrimarySave) return;
    setIsSubmitting(true);
    try {
      await onPrimarySave();
      handleClose();
    } catch {
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    if (onCancel) onCancel();
    handleClose();
  };

  const getTextByMode = (mode: ModalMode): ReactNode => {
    switch (mode) {
      case 'DISTRIBUTED':
        return (
          <>
            <Text textStyle="h4.medium" mb="10px">
              Arbeitsblatt bereits verteilt
            </Text>
            <Text mb="5px" textStyle="body.m.regular">
              Wenn du dieses Arbeitsblatt anpasst und speicherst, passiert Folgendes:
            </Text>
            <Text textStyle="body.m.regular">
              <Text as="span" textStyle="body.m.medium">
                Schüler:innen
              </Text>{' '}
              - alle abgegebenen Antworten werden gelöscht.
            </Text>
            <Text mt="15px" textStyle="body.m.regular">
              Möchtest du stattdessen eine Kopie erstellen und dort weiterarbeiten?
            </Text>
          </>
        );
      case 'DISTRIBUTED_TEMPLATE':
        return (
          <>
            <Text textStyle="h4.medium" mb="10px">
              Arbeitsblatt bereits verteilt & als Vorlage verwendet
            </Text>
            <Text mb="5px" textStyle="body.m.regular">
              Wenn du dieses Arbeitsblatt anpasst und speicherst, passiert Folgendes:
            </Text>
            <Text textStyle="body.m.regular">
              <Text as="span" textStyle="body.m.medium">
                Schüler:innen
              </Text>{' '}
              - alle abgegebenen Antworten werden gelöscht.
            </Text>
            <Text textStyle="body.m.regular">
              <Text as="span" fontWeight="medium">
                Organisationsmitglieder
              </Text>{' '}
              - sehen sofort die aktualisierte Version.
            </Text>
            <Text mt="15px" textStyle="body.m.regular">
              Möchtest du stattdessen eine Kopie erstellen und dort weiterarbeiten?
            </Text>
          </>
        );
      case 'TEMPLATE_ONLY':
        return (
          <>
            <Text textStyle="h4.medium" mb="10px">
              Arbeitsblatt wird als Vorlage verwendet
            </Text>
            <Text mb="5px" textStyle="body.m.regular">
              Wenn du dieses Arbeitsblatt speicherst, passiert Folgendes:
            </Text>
            <Text textStyle="body.m.regular">
              <Text as="span" textStyle="body.m.medium">
                Organisationsmitglieder{' '}
              </Text>
              sehen sofort die aktualisierte Vorlagenversion. Die bisherige Version ist danach nicht
              mehr verfügbar.
            </Text>
            <Text mt="15px" textStyle="body.m.regular">
              Möchtest du stattdessen eine Kopie erstellen und dort weiterarbeiten?
            </Text>
          </>
        );
      default:
        return null;
    }
  };

  const getButtonsByMode = (mode: ModalMode): ReactNode => {
    switch (mode) {
      case 'DISTRIBUTED':
        return (
          <>
            <Button
              variant="primary"
              onClick={handleCopyAndSave}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Kopie erstellen & speichern
            </Button>

            <Button
              variant="destructiveOutline"
              onClick={handlePrimarySave}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              <NovaIcon icon={DeleteIcon} size="m" color="icon.error" aria-hidden />
              Bisherige Antworten löschen & speichern
            </Button>
            <Button onClick={handleCancel} variant="tertiary" disabled={isSubmitting}>
              Zurück
            </Button>
          </>
        );
      case 'DISTRIBUTED_TEMPLATE':
        return (
          <>
            <Button
              variant="primary"
              onClick={handleCopyAndSave}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              <NovaIcon icon={ContentCopyIcon} size="m" color="icon.onAccent" aria-hidden />
              Kopie erstellen & speichern
            </Button>

            <Button
              variant="destructiveOutline"
              onClick={handlePrimarySave}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              <NovaIcon icon={DeleteIcon} size="m" color="icon.error" aria-hidden />
              Bisherige Antworten löschen & speichern
            </Button>
            <Button onClick={handleCancel} variant="tertiary" disabled={isSubmitting}>
              Zurück
            </Button>
          </>
        );
      case 'TEMPLATE_ONLY':
        return (
          <>
            <Button
              variant="primary"
              onClick={handleCopyAndSave}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              <NovaIcon icon={ContentCopyIcon} size="m" color="icon.onAccent" aria-hidden />
              Kopie erstellen & speichern
            </Button>
            <Button
              variant="primary"
              onClick={handlePrimarySave}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              <NovaIcon icon={FilesIcon} size="m" color="icon.onAccent" aria-hidden />
              Vorlage aktualisieren
            </Button>
            <Button onClick={handleCancel} variant="tertiary" disabled={isSubmitting}>
              Zurück
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <CustomModal
      isOpen={isShown}
      onClose={handleCancel}
      placement="center"
      size="md"
      hideHeader={!isDesktop}
    >
      <CustomModalContent
        p={{ base: '0 10px 10px', md: '0 40px 30px' }}
        display="flex"
        flexDirection="column"
      >
        <Box
          p={{ base: 'l', md: '0' }}
          textAlign={{ base: 'center', md: 'left' }}
          mb={{ md: '30px' }}
        >
          {getTextByMode(mode)}
        </Box>
        <Flex
          justifyContent="flex-end"
          w="full"
          h="fit-content"
          borderTop="none"
          borderColor="border.accent"
          p="0"
          flexDirection="column"
          gap="10px"
        >
          {getButtonsByMode(mode)}
        </Flex>
      </CustomModalContent>
    </CustomModal>
  );
};
